import { TextFormatAlign } from "../TextFormatAlign";
import { TextFieldAutoSize } from "../TextFieldAutoSize";
import { TextFormat } from "../TextFormat";
import { FontRestrictionType, TextDrawingState, RichTextInputToken } from "./dataTypes";
import { layOutTokens, LayedOutText } from "./layoutText";

export type TextSelection = { readonly start: number, readonly end: number };

export type LayoutParam = ({
    readonly autoSize: TextFieldAutoSize.NONE;
    readonly x: number;
    readonly y: number;
    readonly lineWidth: number | null;
} | {
    readonly autoSize: TextFieldAutoSize.LEFT | TextFieldAutoSize.CENTER | TextFieldAutoSize.RIGHT;
    readonly x: number;
    readonly y: number;
}) & {
    readonly wordWrap: boolean;
    readonly multiline: boolean;
    readonly width: number; // when autoSize is used (non "none") this is when wordWrap=true, see https://help.adobe.com/en_US/FlashPlatform/reference/actionscript/3/flash/text/TextField.html#autoSize
    readonly height: number; // see above

    readonly previousWidth: number;
    readonly previousX: number;
    readonly firstVisibleLineIndex: number;
    readonly verticalAlign: "top" | "middle" | "bottom";
};

export type LatestLayoutType = LayedOutText & {
    readonly position: { x: number, y: number },
    readonly size: { width: number, height: number },
    readonly bounds: createjs.Rectangle,
    readonly autoSizeBehavior: TextFieldAutoSize
};

export function calculateLatestLayoutType(this: void, ctx: CanvasRenderingContext2D, layoutParams: LayoutParam, initialFormat: TextFormat, inputTokens: RichTextInputToken[], fontRestriction: FontRestrictionType | null): LatestLayoutType {
    let maxWidth: number | "textWidth";
    let maxHeight: number;
    if (layoutParams.autoSize === TextFieldAutoSize.NONE) {
        maxWidth = layoutParams.lineWidth || layoutParams.width;
        maxHeight = layoutParams.height;
    } else {
        maxWidth = layoutParams.wordWrap ? layoutParams.width : "textWidth";
        maxHeight = 9999;
    }

    const latestLayoutResult = layOutTokens(ctx, inputTokens, initialFormat, maxWidth, fontRestriction, layoutParams.multiline, layoutParams.wordWrap, maxHeight, layoutParams.firstVisibleLineIndex, layoutParams.verticalAlign);

    let width: number;
    let height: number;
    let x: number;
    let y: number;
    {
        const metrics = latestLayoutResult.metrics;
        const requiredWidth = layoutParams.wordWrap ? layoutParams.width : metrics.textWithGutterWidth;
        const requiredHeight = metrics.textWithGutterHeight;

        if (layoutParams.autoSize === TextFieldAutoSize.NONE || layoutParams.autoSize === undefined || layoutParams.wordWrap) {
            // don't resize the `TextField` if wordWrap is true, see https://help.adobe.com/en_US/FlashPlatform/reference/actionscript/3/flash/text/TextField.html#autoSize
            x = layoutParams.x;
            y = layoutParams.y;
            width = layoutParams.width;
            height = layoutParams.autoSize === TextFieldAutoSize.NONE ? layoutParams.height : requiredHeight; // Extend bottom of TF if `wordWrap` is `true`
        } else {
            switch (layoutParams.autoSize) {
                case TextFieldAutoSize.LEFT: {
                    // nothing to do with `x`
                    // still update the width/height
                    x = layoutParams.x;
                    y = layoutParams.y;
                    width = requiredWidth;
                    height = requiredHeight;
                    break;
                }
                case TextFieldAutoSize.RIGHT: {
                    const nextX = layoutParams.previousX + layoutParams.previousWidth - requiredWidth;
                    x = nextX;
                    y = layoutParams.y;
                    width = requiredWidth;
                    height = requiredHeight;
                    break;
                }
                case TextFieldAutoSize.CENTER: {
                    const nextX = layoutParams.previousX + layoutParams.previousWidth / 2 - requiredWidth / 2;
                    x = nextX;
                    y = layoutParams.y;
                    width = requiredWidth;
                    height = requiredHeight;
                    break;
                }
                default:
                    throw new Error(`unexpect autosize property`);
            }
        }
    }

    const res: LatestLayoutType = {
        ...latestLayoutResult,
        autoSizeBehavior: layoutParams.autoSize,
        bounds: new createjs.Rectangle(0, 0, width, height),
        position: {
            x: x,
            y: y,
        },
        size: {
            width: width,
            height: height,
        }
    };

    return res;
}
